import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import { PointerDragBehavior } from "@babylonjs/core/Behaviors/Meshes/pointerDragBehavior";
import { ActionManager } from "@babylonjs/core/Actions/actionManager";
import { ExecuteCodeAction } from "@babylonjs/core/Actions/directActions";
import type { InteractableDef, PanelDef } from "./schema";
import { openPanel } from "./PanneauBiblique";
import type { Hud } from "./Hud";
import { t } from "./i18n";

export type InteractionEvent =
  | { kind: "panel"; id: string }
  | { kind: "collect"; id: string }
  | { kind: "deliver"; id: string; targetId: string }
  | { kind: "lever"; id: string };

export interface InteractionContext {
  scene: Scene;
  hud: Hud;
  highlight: HighlightLayer;
  targets: Map<string, Mesh>;
  panels: Map<string, PanelDef>;
  onEvent: (e: InteractionEvent) => void;
}

/** Wires interactivity (hover, click, drag-to-target) onto interactable meshes. */
export class InteractionSystem {
  constructor(private readonly ctx: InteractionContext) {}

  attach(mesh: Mesh, def: InteractableDef): void {
    mesh.isPickable = true;
    this.addHover(mesh);

    switch (def.type) {
      case "panel":
        this.onClick(mesh, () => {
          const p = def.panelId ? this.ctx.panels.get(def.panelId) : undefined;
          if (p) openPanel(this.ctx.hud.adt, p);
          this.ctx.onEvent({ kind: "panel", id: def.id });
        });
        break;
      case "npc_dialog":
        this.onClick(mesh, () => {
          openPanel(this.ctx.hud.adt, this.npcPanel(def));
          this.ctx.onEvent({ kind: "panel", id: def.id });
        });
        break;
      case "pickup":
        this.onClick(mesh, () => {
          this.collectAnim(mesh);
          this.ctx.onEvent({ kind: "collect", id: def.id });
        });
        break;
      case "lever_action":
        this.onClick(mesh, () => {
          this.pulse(mesh);
          this.ctx.onEvent({ kind: "lever", id: def.id });
        });
        break;
      case "drag_drop":
      case "guide_target":
      case "build_slot":
        // A build_slot that is itself a target is not draggable; pieces linked to
        // a target are. If this def has a targetId pointing elsewhere, it is draggable.
        if (def.targetId && def.targetId !== def.id) this.makeDraggable(mesh, def);
        break;
    }
  }

  /** reRoot()-composed shapes (stele/gate/flame/person) have an EMPTY root:
   *  Babylon picks the CHILD geometry, so hover/click must be wired on every
   *  mesh of the hierarchy, not just the root (public deep-test finding). */
  private allMeshes(mesh: Mesh): Mesh[] {
    const kids = mesh.getChildMeshes(false).filter((m): m is Mesh => m instanceof Mesh);
    kids.forEach((k) => (k.isPickable = true));
    return [mesh, ...kids];
  }

  private addHover(root: Mesh): void {
    for (const mesh of this.allMeshes(root)) this.addHoverOne(mesh, root);
  }

  private addHoverOne(mesh: Mesh, root: Mesh): void {
    void root;
    mesh.actionManager = mesh.actionManager ?? new ActionManager(this.ctx.scene);
    mesh.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, () => {
        this.ctx.highlight.addMesh(mesh, Color3.FromHexString("#ffe08a"));
        childMeshes(mesh).forEach((c) => this.ctx.highlight.addMesh(c, Color3.FromHexString("#ffe08a")));
        this.ctx.scene.getEngine().getRenderingCanvas()!.style.cursor = "pointer";
      }),
    );
    mesh.actionManager.registerAction(
      new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, () => {
        this.ctx.highlight.removeMesh(mesh);
        childMeshes(mesh).forEach((c) => this.ctx.highlight.removeMesh(c));
        this.ctx.scene.getEngine().getRenderingCanvas()!.style.cursor = "default";
      }),
    );
  }

  private onClick(root: Mesh, cb: () => void): void {
    for (const mesh of this.allMeshes(root)) {
      mesh.actionManager = mesh.actionManager ?? new ActionManager(this.ctx.scene);
      mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger, cb));
    }
  }

  private makeDraggable(mesh: Mesh, def: InteractableDef): void {
    const drag = new PointerDragBehavior({ dragPlaneNormal: new Vector3(0, 1, 0) });
    drag.useObjectOrientationForDragging = false;
    let delivered = false;
    mesh.addBehavior(drag);
    drag.onDragObservable.add(() => {
      mesh.position.y = Math.max(mesh.position.y, 0.05);
    });
    drag.onDragEndObservable.add(() => {
      if (delivered) return;
      const target = def.targetId ? this.ctx.targets.get(def.targetId) : undefined;
      if (!target) return;
      const d = Vector3.Distance(
        new Vector3(mesh.position.x, 0, mesh.position.z),
        new Vector3(target.position.x, 0, target.position.z),
      );
      const radius = (target.scaling.x || 1) * 3 + 3.5;
      if (d <= radius) {
        delivered = true;
        mesh.removeBehavior(drag);
        this.deliverAnim(mesh, target);
        this.ctx.onEvent({ kind: "deliver", id: def.id, targetId: def.targetId! });
      }
    });
  }

  private npcPanel(def: InteractableDef): PanelDef {
    return {
      id: def.id,
      title: def.label,
      reference: "",
      passage: def.label,
      explain: { fr: "Un personnage te parle. Écoute et choisis avec sagesse.", en: "A character speaks with you. Listen and choose wisely." },
    };
  }

  private collectAnim(mesh: Mesh): void {
    const start = performance.now();
    const y0 = mesh.position.y;
    const obs = this.ctx.scene.onBeforeRenderObservable.add(() => {
      const k = (performance.now() - start) / 500;
      mesh.position.y = y0 + Math.sin(Math.min(k, 1) * Math.PI) * 1.2;
      mesh.scaling.setAll(Math.max(0.01, 1 - k));
      if (k >= 1) {
        mesh.setEnabled(false);
        this.ctx.scene.onBeforeRenderObservable.remove(obs);
      }
    });
  }

  private deliverAnim(mesh: Mesh, target: Mesh): void {
    const start = performance.now();
    const from = mesh.position.clone();
    const to = new Vector3(target.position.x, 0.2, target.position.z + 1.4);
    const obs = this.ctx.scene.onBeforeRenderObservable.add(() => {
      const k = Math.min(1, (performance.now() - start) / 600);
      mesh.position = Vector3.Lerp(from, to, k);
      if (k >= 1) {
        mesh.scaling.setAll(0.5);
        mesh.setEnabled(false);
        this.ctx.scene.onBeforeRenderObservable.remove(obs);
      }
    });
  }

  private pulse(mesh: Mesh): void {
    const start = performance.now();
    const obs = this.ctx.scene.onBeforeRenderObservable.add(() => {
      const k = (performance.now() - start) / 400;
      const s = 1 + Math.sin(Math.min(k, 1) * Math.PI) * 0.25;
      mesh.scaling.setAll(s);
      if (k >= 1) {
        mesh.scaling.setAll(1);
        this.ctx.scene.onBeforeRenderObservable.remove(obs);
      }
    });
  }
}

function childMeshes(mesh: Mesh): Mesh[] {
  return mesh.getChildMeshes(false).filter((m): m is Mesh => m instanceof Mesh);
}

/** helper kept for parity with i18n usage in dialogs */
export const _tref = t;
